import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Wand2, Square, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const STATE = {
  waiting: [Clock, "text-stone-400", "Waiting"],
  running: [Loader2, "text-[#9A7B2C] animate-spin", "Rewriting"],
  done: [CheckCircle2, "text-emerald-600", "Done"],
  budget: [AlertTriangle, "text-amber-600", "Budget used up"],
  stopped: [Square, "text-stone-500", "Stopped"],
  skipped: [AlertTriangle, "text-stone-500", "Skipped"],
  failed: [AlertTriangle, "text-red-600", "Failed"],
};

const KitLine = ({ k, i }) => {
  const [Icon, cls, base] = STATE[k.state] || STATE.waiting;
  const label = k.state === "running" && k.phase === "fill" ? `Filling ${k.fill_total || k.fill} page(s), then re-checking` : base;
  return (
    <li data-testid={`edition-rewrite-kit-${i}`} className="flex items-start gap-2 border-t border-stone-100 py-1.5 text-sm">
      <Icon size={14} className={`mt-0.5 shrink-0 ${cls}`} />
      <div className="min-w-0">
        <Link to={`/kits/${k.id}`} className="text-[#2B2A26] hover:text-[#9A7B2C]">{k.destination}</Link>
        <span className="ml-2 text-xs text-stone-500">{label} · {k.weak} weak page{k.weak === 1 ? "" : "s"}{k.fill ? ` (${k.fill} to fill first)` : ""}
          {k.done !== undefined && ` · ${k.done} rewritten${k.failed ? `, ${k.failed} failed` : ""}${k.left ? `, ${k.left} left` : ""}`}</span>
        {k.note && <div className="text-xs text-stone-500">{k.note}</div>}
      </div>
    </li>
  );
};

const RunBox = ({ run, onStop, busy }) => {
  const running = run.status === "running";
  const finished = run.kits.filter((k) => !["waiting", "running"].includes(k.state)).length;
  return (
    <div data-testid="edition-rewrite-run" className="mt-3 rounded-md border border-stone-200 bg-white p-3">
      <div className="flex flex-wrap items-center gap-3">
        <b className="text-sm text-[#2B2A26]">{run.label}</b>
        <span data-testid="edition-rewrite-status" className="text-sm text-stone-600">
          {running ? `Kit ${Math.min(finished + 1, run.kits.length)} of ${run.kits.length}` : `${run.status === "done" ? "Finished" : "Stopped"} — ${finished} of ${run.kits.length} kits processed`}
        </span>
        {running && (
          <button data-testid="edition-rewrite-stop" disabled={busy || run.stop} onClick={onStop} className="ml-auto inline-flex items-center gap-1 text-xs text-stone-600 hover:text-black disabled:opacity-50">
            <Square size={11} /> {run.stop ? "Stopping…" : "Stop after the current step"}
          </button>
        )}
      </div>
      {running && <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden"><div className="h-full bg-[#C9A847] transition-[width] duration-500" style={{ width: `${(finished / run.kits.length) * 100}%` }} /></div>}
      <ul className="mt-2 max-h-64 overflow-y-auto">{run.kits.map((k, i) => <KitLine key={k.id} k={k} i={i} />)}</ul>
    </div>
  );
};

export const EditionRewrite = ({ editions }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get("/edition-rewrite", { params: { summary: true } }).then((x) => setD(x.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = d?.run?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [running, load]);
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.message(msg); await load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (!d) return <Loader2 size={16} className="animate-spin mt-3 text-[#9A7B2C]" />;
  return (
    <div data-testid="edition-rewrite" className="mt-5 border-t border-stone-200 pt-4">
      <b className="text-sm text-[#2B2A26]">Rewrite weak pages across an edition</b>
      <p className="text-xs text-stone-500 mt-0.5">Goes kit by kit: first fills weak pages that aren't full enough and re-checks them, then rewrites only the pages still below 70. Filling and rewriting each get their own $8 per kit; if one runs out, the leftover pages are listed and the run moves on.</p>
      <ul className="mt-2">
        {editions.map((ed) => {
          const s = d.summary?.[ed.key] || { weak_pages: 0, weak_kits: 0 };
          return (
            <li key={ed.key} className="flex flex-wrap items-center gap-3 py-1.5 text-sm">
              <span className="w-40 text-[#2B2A26]">{ed.label}</span>
              <span data-testid={`edition-weak-${ed.key}`} className="text-xs text-stone-500">
                {s.weak_pages ? `${s.weak_pages} weak page(s) in ${s.weak_kits} kit(s)${s.fill_pages ? ` · ${s.fill_pages} filled first` : ""}` : "No weak pages to rewrite"}
                {s.busy ? ` · ${s.busy} busy kit(s) left out` : ""}{s.already ? ` · ${s.already} already rewritten once (use Accept as is)` : ""}
              </span>
              {s.weak_pages > 0 && (
                <button data-testid={`edition-rewrite-btn-${ed.key}`} disabled={busy || running}
                  onClick={() => act(() => http.post(`/edition-rewrite/${ed.key}`), `Rewriting ${s.weak_pages} page(s) across ${s.weak_kits} ${ed.label} kit(s) — you can leave this page`)}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-sm bg-[#2B2A26] px-3 py-1.5 text-xs font-bold text-white hover:bg-black transition-colors disabled:opacity-50">
                  <Wand2 size={12} /> Rewrite all weak pages
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {d.run && <RunBox run={d.run} busy={busy} onStop={() => act(() => http.post("/edition-rewrite-stop"), "Stopping after the current page")} />}
    </div>
  );
};
